import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/context/NotificationContext";
import { Bell, Check, CheckCheck } from "lucide-react";

const PRIORITY_DOT = {
  critical: "bg-red-500", high: "bg-amber-500", normal: "bg-primary", low: "bg-slate-400",
};

export function NotificationCenter() {
  const { items, unread, markRead, markAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 bg-popover text-popover-foreground rounded-xl border border-border shadow-lg z-40 overflow-hidden"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-semibold text-sm">Notifications</span>
            {unread > 0 && (
              <button
                onClick={markAll}
                className="text-xs text-primary hover:underline flex items-center gap-1"
                data-testid="notification-mark-all"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications</p>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-border flex gap-3 ${n.read ? "" : "bg-muted/40"}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[n.priority] || PRIORITY_DOT.normal}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{n.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{n.message}</p>
                  </div>
                  {!n.read && (
                    <button
                      onClick={() => markRead(n.id)}
                      className="p-1 rounded hover:bg-muted text-muted-foreground"
                      title="Mark read"
                      data-testid={`notification-read-${n.id}`}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
          <button
            onClick={() => { setOpen(false); navigate("/admin/notifications"); }}
            className="w-full px-4 py-2.5 text-sm text-primary hover:bg-muted transition-colors font-medium"
            data-testid="notification-view-all"
          >
            View all
          </button>
        </div>
      )}
    </div>
  );
}
